/**
 * Collector sweep surface — work the project's epic/fixVersion nets caught that
 * lacks the membership label. Stamp writes the ws-label back to Jira; dismiss
 * records "not this project" so the sweep never re-suggests it.
 */
import { useCallback, useEffect, useState } from "react";
import { Box, Button } from "@mui/material";
import { useSnackbar } from "notistack";
import { brainTokens } from "../../design/brainTheme";
import { StatusPill } from "../../mui";
import {
  getProjectCollectors,
  stampCollectorIssues,
  dismissCollectorIssue,
  type CollectorCandidate,
} from "../../lib/brain-api";

const c = brainTokens.color;
const MONO = brainTokens.font.mono;

export function UnlabeledWorkPanel({
  projectId,
  isAdmin,
  onChanged,
  embedded = false,
  onCount,
}: {
  projectId: string;
  isAdmin: boolean;
  onChanged: () => void;
  /** Strip row list only (no panel chrome) for AdminStrips. */
  embedded?: boolean;
  onCount?: (n: number) => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [label, setLabel] = useState<string | null>(null);
  const [rows, setRows] = useState<CollectorCandidate[]>([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await getProjectCollectors(projectId);
      setLabel(r.label);
      setRows(r.candidates);
      onCount?.(r.candidates.length);
    } catch {
      setRows([]);
      onCount?.(0);
    }
  }, [projectId, onCount]);

  useEffect(() => {
    void load();
  }, [load]);

  const stamp = async (keys: string[]) => {
    setBusy(true);
    try {
      const r = await stampCollectorIssues(projectId, keys);
      enqueueSnackbar(`Stamped ${r.stamped.length} issue${r.stamped.length === 1 ? "" : "s"} with ${r.label}.`, {
        variant: "success",
      });
      await load();
      onChanged();
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Stamp failed", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  const dismiss = async (key: string) => {
    setBusy(true);
    try {
      await dismissCollectorIssue(projectId, key);
      await load();
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Dismiss failed", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  if (embedded) {
    if (!rows.length) return null;
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
        {rows.map((r) => (
          <Box
            key={r.key}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              py: "6px",
              minWidth: 0,
            }}
          >
            <Box
              component="a"
              href={r.url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                fontFamily: MONO,
                fontSize: 12.5,
                color: c.tealInk,
                textDecoration: "none",
                width: 62,
                flex: "none",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {r.key}
            </Box>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                fontSize: 13,
                color: c.muted,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {r.summary}
            </Box>
            <Box sx={{ fontSize: 11.5, color: c.muted2, flex: "none", whiteSpace: "nowrap" }}>
              via {r.collector}
            </Box>
            {isAdmin && (
              <>
                <Box
                  component="button"
                  type="button"
                  disabled={busy}
                  onClick={() => void stamp([r.key])}
                  sx={{
                    border: "none",
                    background: "transparent",
                    cursor: busy ? "default" : "pointer",
                    fontSize: 12.5,
                    fontFamily: brainTokens.font.sans,
                    fontWeight: 500,
                    color: c.tealInk,
                    p: 0,
                    flex: "none",
                  }}
                >
                  Stamp
                </Box>
                <Box
                  component="button"
                  type="button"
                  disabled={busy}
                  onClick={() => void dismiss(r.key)}
                  sx={{
                    border: "none",
                    background: "transparent",
                    cursor: busy ? "default" : "pointer",
                    fontSize: 12.5,
                    fontFamily: brainTokens.font.sans,
                    color: c.muted2,
                    p: 0,
                    flex: "none",
                  }}
                >
                  Not this project
                </Box>
              </>
            )}
          </Box>
        ))}
      </Box>
    );
  }

  if (!rows.length) return null;

  return (
    <Box sx={{ background: c.panelBg, border: `1px solid ${c.borderLight}`, borderRadius: "12px", p: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Box
          sx={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: c.muted2,
            flex: 1,
          }}
        >
          Unlabeled work caught by collectors · {rows.length}
        </Box>
        {isAdmin && (
          <Button size="small" variant="contained" disabled={busy} onClick={() => void stamp(rows.map((r) => r.key))}>
            Stamp all as {label}
          </Button>
        )}
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {rows.map((r) => (
          <Box
            key={r.key}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontSize: 13,
              py: 0.5,
              borderBottom: `1px solid ${c.borderLight}`,
              "&:last-child": { borderBottom: "none" },
            }}
          >
            <Box
              component="a"
              href={r.url}
              target="_blank"
              rel="noopener"
              sx={{ fontFamily: brainTokens.font.mono, fontSize: 12.5, color: c.tealInk, textDecoration: "none" }}
            >
              {r.key}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: c.ink2 }}>
              {r.summary}
            </Box>
            <StatusPill tone={r.statusCategory === "done" ? "neutral" : "teal"} label={r.status} />
            <Box sx={{ fontSize: 11.5, color: c.faint }}>via {r.collector}</Box>
            {isAdmin && (
              <>
                <Button size="small" sx={{ minWidth: 0 }} disabled={busy} onClick={() => void stamp([r.key])}>
                  Stamp
                </Button>
                <Button
                  size="small"
                  color="inherit"
                  sx={{ minWidth: 0, color: c.muted2 }}
                  disabled={busy}
                  onClick={() => void dismiss(r.key)}
                >
                  Not this project
                </Button>
              </>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
}
